import {
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Avatar,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Edit as EditIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

function EditWorkflowHazardsDialog({
  open,
  onClose,
  onSave,
  workflow,
  allHazards,
  allWorkflows,
  selectedHazardIds,
  onToggleHazard,
  isLoading,
  hazardsLoading,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  // Filter workflows that have target "WORK_PERMIT"
  const workPermitWorkflows =
    allWorkflows?.filter((wf) => wf.target === "WORK_PERMIT") || [];

  // Group hazards by workflow for the dialog
  const workflowGroups = [];

  // Add all workflows (including the one being edited)
  workPermitWorkflows.forEach((wf) => {
    const workflowHazards = wf.hazards || [];
    workflowGroups.push({
      workflow: wf,
      hazards: workflowHazards,
      isCurrentWorkflow: wf.id === workflow?.id,
    });
  });

  // Add unassigned hazards group
  const unassignedHazards = allHazards?.filter((hazard) => {
    // Check if this hazard is not linked to any workflow
    const isLinkedToAnyWorkflow = workPermitWorkflows.some((wf) =>
      wf.hazards?.some((workflowHazard) => workflowHazard.id === hazard.id)
    );
    return !isLinkedToAnyWorkflow;
  });

  if (unassignedHazards && unassignedHazards.length > 0) {
    workflowGroups.push({
      workflow: {
        id: "unassigned",
        name: t("hazards.unassignedHazards"),
      },
      hazards: unassignedHazards,
      isCurrentWorkflow: false,
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: theme.palette.checklist.surface.subtle,
          borderBottom: 1,
          borderColor: theme.palette.checklist.border.subtle,
          pb: 1.5,
          pt: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "warning.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "warning.contrastText",
            }}
          >
            <EditIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
              {t("worktypeHazards.editDialogTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("worktypeHazards.editDialogSubtitle", {
                workflowName: workflow?.name,
              })}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              bgcolor: theme.palette.checklist.interactive.hover,
              "&:hover": {
                bgcolor: theme.palette.checklist.interactive.selected,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 3,
          bgcolor: theme.palette.checklist.surface.elevated,
        }}
      >
        {hazardsLoading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={32} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {t("worktypeHazards.loadingHazards")}
            </Typography>
          </Box>
        ) : allHazards && allHazards.length > 0 ? (
          <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: theme.palette.primary.main,
              }}
            >
              {t("worktypeHazards.selectHazards")}
            </Typography>
            <Box>
              {workflowGroups.map((group, groupIndex) => (
                <Box key={group.workflow.id} sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 1.5,
                      color: group.isCurrentWorkflow
                        ? theme.palette.success.main
                        : group.workflow.id === "unassigned"
                          ? theme.palette.text.secondary
                          : theme.palette.info.main,
                      px: 1,
                    }}
                  >
                    {group.workflow.name}
                  </Typography>
                  {group.hazards.length > 0 ? (
                    <List sx={{ p: 0 }}>
                      {group.hazards.map((hazard, index) => {
                        const isChecked = selectedHazardIds.includes(hazard.id);
                        return (
                          <Box key={hazard.id}>
                            <ListItem
                              sx={{
                                p: 1.5,
                                py: 1,
                                transition: "all 0.2s ease",
                                bgcolor: isChecked
                                  ? alpha(theme.palette.success.main, 0.05)
                                  : "transparent",
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.04
                                  ),
                                },
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isChecked}
                                    onChange={() => onToggleHazard(hazard.id)}
                                    color="primary"
                                  />
                                }
                                label={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        bgcolor: alpha(
                                          theme.palette.error.main,
                                          0.1
                                        ),
                                        border: "2px solid",
                                        borderColor: alpha(
                                          theme.palette.error.main,
                                          0.2
                                        ),
                                        width: 32,
                                        height: 32,
                                      }}
                                    >
                                      <WarningIcon
                                        sx={{
                                          color: "error.main",
                                          fontSize: 16,
                                        }}
                                      />
                                    </Avatar>
                                    <Typography
                                      variant="body2"
                                      fontWeight="500"
                                    >
                                      {hazard.name}
                                    </Typography>
                                  </Box>
                                }
                                sx={{ width: "100%", m: 0 }}
                              />
                            </ListItem>
                            {index < group.hazards.length - 1 && (
                              <Divider sx={{ my: 0.5, opacity: 0.3 }} />
                            )}
                          </Box>
                        );
                      })}
                    </List>
                  ) : (
                    <Box
                      sx={{
                        p: 2,
                        textAlign: "center",
                        bgcolor: alpha(theme.palette.grey[500], 0.05),
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t("hazards.noHazardsInWorkflow")}
                      </Typography>
                    </Box>
                  )}
                  {groupIndex < workflowGroups.length - 1 && (
                    <Divider sx={{ mt: 2 }} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t("worktypeHazards.noHazardsAvailable")}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          p: 3,
          gap: 2,
          bgcolor: theme.palette.checklist.surface.elevated,
          borderTop: 1,
          borderColor: theme.palette.checklist.border.subtle,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          color="primary"
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: "none",
            fontWeight: "medium",
          }}
        >
          {t("worktypeHazards.cancel")}
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          color="warning"
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <EditIcon />
            )
          }
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: "none",
            fontWeight: "bold",
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
              transform: "translateY(-1px)",
            },
          }}
        >
          {t("worktypeHazards.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditWorkflowHazardsDialog;
